import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Service Functions
export const apiService = {
  // Authentication
  auth: {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    getProfile: () => api.get('/auth/profile'),
  },

  // Menu
  getMenu: () => api.get('/menu'),
  toggleMenuItem: (key, show) => api.post('/menu/toggle', { key, show }),

  // News
  news: {
    getAll: () => api.get('/news'),
    getById: (id) => api.get(`/news/${id}`),
    create: (data) => api.post('/news', data),
    update: (id, data) => api.put(`/news/${id}`, data),
    delete: (id) => api.delete(`/news/${id}`),
  },

  // Events
  events: {
    getAll: () => api.get('/events'),
    getUpcoming: () => api.get('/events/upcoming'),
    create: (data) => api.post('/events', data),
    update: (id, data) => api.put(`/events/${id}`, data),
    delete: (id) => api.delete(`/events/${id}`),
  },

  // Gallery
  gallery: {
    getAll: () => api.get('/gallery'),
    getAllForAdmin: () => api.get('/gallery/all'),
    getByCategory: (category) => api.get(`/gallery/category/${category}`),
    create: (data) => api.post('/gallery', data),
    update: (id, data) => api.put(`/gallery/${id}`, data),
    delete: (id) => api.delete(`/gallery/${id}`),
  },

  // Staff
  staff: {
    getAll: () => api.get('/staff'),
    search: (query) => api.get(`/staff/search/${query}`),
    add: (data) => api.post('/staff', data),
    update: (id, data) => api.put(`/staff/${id}`, data),
    delete: (id) => api.delete(`/staff/${id}`),
  },

  // Admissions
  admissions: {
    getAll: () => api.get('/admissions'),
    submit: (data) => api.post('/admissions', data),
    update: (id, data) => api.put(`/admissions/${id}`, data),
    delete: (id) => api.delete(`/admissions/${id}`),
  },

  // Timetable
  timetable: {
    getAll: () => api.get('/timetable'),
    getByClassAndDay: (className, day) => api.get(`/timetable/${className}/${day}`),
    create: (data) => api.post('/timetable', data),
    delete: (id) => api.delete(`/timetable/${id}`),
  },

  // Fees
  fees: {
    getAll: () => api.get('/fees'),
    getByClass: (className) => api.get(`/fees/class/${className}`),
    calculate: (className) => api.get(`/fees/calculate/${className}`),
    create: (data) => api.post('/fees', data),
    update: (id, data) => api.put(`/fees/${id}`, data),
    delete: (id) => api.delete(`/fees/${id}`),
  },

  // Downloads
  downloads: {
    getAll: () => api.get('/downloads'),
    getAllAdmin: () => api.get('/downloads/all'),
    getByCategory: (category) => api.get(`/downloads/category/${category}`),
    incrementCount: (id) => api.post(`/downloads/${id}/download`),
    create: (data) => api.post('/downloads', data),
    update: (id, data) => api.put(`/downloads/${id}`, data),
    delete: (id) => api.delete(`/downloads/${id}`),
  },

  // FAQ
  faq: {
    getAll: () => api.get('/faq'),
    getAllAdmin: () => api.get('/faq/all'),
    getByCategory: (category) => api.get(`/faq/category/${category}`),
    search: (query) => api.get(`/faq/search/${query}`),
    create: (data) => api.post('/faq', data),
    add: (data) => api.post('/faq', data),
    update: (id, data) => api.put(`/faq/${id}`, data),
    delete: (id) => api.delete(`/faq/${id}`),
  },

  // Testimonials
  testimonials: {
    getAll: () => api.get('/testimonials'),
    getFeatured: () => api.get('/testimonials/featured'),
    submit: (data) => api.post('/testimonials', data),
    /** Admin JWT: creates with isApproved/featured (public POST always pending). */
    createAdmin: (data) => api.post('/testimonials/admin', data),
    update: (id, data) => api.put(`/testimonials/${id}`, data),
    approve: (id) => api.put(`/testimonials/${id}/approve`),
    delete: (id) => api.delete(`/testimonials/${id}`),
  },

  // Social Links
  social: {
    getAll: () => api.get('/social'),
    add: (data) => api.post('/social', data),
    update: (id, data) => api.put(`/social/${id}`, data),
    delete: (id) => api.delete(`/social/${id}`),
  },

  // Alerts
  alert: {
    getAll: () => api.get('/alert'),
    getEmergency: () => api.get('/alert/emergency'),
    create: (data) => api.post('/alert', data),
    update: (id, data) => api.put(`/alert/${id}`, data),
    delete: (id) => api.delete(`/alert/${id}`),
  },

  // Newsletter
  newsletter: {
    subscribe: (email, name) => api.post('/newsletter/subscribe', { email, name }),
    unsubscribe: (email) => api.post(`/newsletter/unsubscribe/${email}`),
    getAll: () => api.get('/newsletter'),
    delete: (id) => api.delete(`/newsletter/${id}`),
  },

  // Settings
  settings: {
    /** Public: school / whatsapp / social — no secrets. */
    getPublicSite: () => api.get('/settings/public-site'),
    /** Public: category=menu only — safe for anonymous visitors (header visibility). */
    getMenuVisibility: () => api.get('/settings/menu-visibility'),
    getAll: () => api.get('/settings'),
    update: (key, data) => api.put(`/settings/${key}`, data),
    bulkUpdate: (settings) => api.put('/settings/bulk/update', { settings }),
    delete: (key) => api.delete(`/settings/${key}`),
  },

  // Dashboard Statistics
  getDashboardStats: () => api.get('/dashboard/stats'),
};

export default api;
